import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const ROLES = [
  ["COMMERCANT", "Commerçant"],
  ["FABRICANT", "Fabricant"],
  ["ADMIN_MAIRIE", "Mairie / Collectivité"],
  ["AGENT_CONTROLE", "Agent de contrôle"],
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [munis, setMunis] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "COMMERCANT", company_name: "", siret: "", phone: "", municipality_id: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/municipalities").then((r) => setMunis(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = { ...f };
      if (!payload.municipality_id) delete payload.municipality_id;
      const u = await register(payload);
      toast.success(`Compte créé — bienvenue ${u.name}`);
      nav("/app");
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };

  const needsMuni = ["ADMIN_MAIRIE", "AGENT_CONTROLE"].includes(f.role);
  const isCompany = ["COMMERCANT", "FABRICANT"].includes(f.role);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F4F4F5]">
      <div className="w-full max-w-xl">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 bg-[#000091] flex items-center justify-center"><span className="text-white font-black text-lg">E</span></div>
          <span className="font-display font-black text-xl">ENSEIGNE SMART</span>
        </Link>
        <div className="card-flat p-8">
          <h2 className="font-display font-black text-3xl tracking-tight mb-6">Créer un compte</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Je suis</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setF({ ...f, role: v })} data-testid={`role-${v}`}
                    className={`border px-3 py-2 text-sm font-medium transition-colors ${f.role === v ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Nom complet</label>
                <input data-testid="reg-name" required value={f.name} onChange={set("name")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Téléphone</label>
                <input data-testid="reg-phone" value={f.phone} onChange={set("phone")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]" />
              </div>
            </div>
            {isCompany && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">{f.role === "FABRICANT" ? "Nom du fabricant" : "Raison sociale"}</label>
                  <input data-testid="reg-company" value={f.company_name} onChange={set("company_name")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">SIRET</label>
                  <input data-testid="reg-siret" value={f.siret} onChange={set("siret")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]" />
                </div>
              </div>
            )}
            {needsMuni && (
              <div>
                <label className="text-sm font-medium block mb-1">Collectivité</label>
                <select data-testid="reg-municipality" required value={f.municipality_id} onChange={set("municipality_id")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]">
                  <option value="">Sélectionner...</option>
                  {munis.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Email</label>
                <input data-testid="reg-email" type="email" required value={f.email} onChange={set("email")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Mot de passe</label>
                <input data-testid="reg-password" type="password" required value={f.password} onChange={set("password")} className="w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]" />
              </div>
            </div>
            {err && <div data-testid="reg-error" className="bg-red-50 border border-red-200 text-[#E1000F] px-4 py-2 text-sm">{err}</div>}
            <button data-testid="reg-submit" disabled={loading} className="w-full bg-[#000091] text-white py-3 font-semibold hover:bg-blue-900 transition-colors disabled:opacity-60">
              {loading ? "Création..." : "Créer mon compte"}
            </button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Déjà inscrit ? <Link to="/login" className="text-[#000091] font-semibold hover:underline">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
